import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { useChildren, useExponentByCode } from '../api/queries'
import { formatMoney, parseMoneyInput } from '../lib/money'
import type { Child } from '../api/types'

const DAYS_OF_WEEK = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']

export function AllowanceAdminPage() {
  const { data: children } = useChildren()
  const queryClient = useQueryClient()
  const [runResult, setRunResult] = useState<string | null>(null)

  const runMutation = useMutation({
    mutationFn: async () => {
      const asOf = new Date().toISOString().slice(0, 10)
      const { data, error } = await api.POST('/allowance/run', { body: { asOf } })
      if (error) throw new Error('Catch-up run failed')
      return data
    },
    onSuccess: (posted) => {
      setRunResult(posted.length === 0 ? 'Nothing was due.' : `Posted ${posted.length} allowance payment(s).`)
      queryClient.invalidateQueries({ queryKey: ['summary'] })
      queryClient.invalidateQueries({ queryKey: ['balance'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['allowance-schedules'] })
    },
  })

  return (
    <div className="allowance-admin">
      <h1>Allowance</h1>
      <button type="button" onClick={() => runMutation.mutate()} disabled={runMutation.isPending}>
        Run catch-up now
      </button>
      {runResult && <p className="hint">{runResult}</p>}

      {(children ?? []).map((child) => (
        <ChildAllowanceSection key={child.id} child={child} />
      ))}
    </div>
  )
}

function ChildAllowanceSection({ child }: { child: Child }) {
  const queryClient = useQueryClient()
  const exponentByCode = useExponentByCode()

  const schedulesQuery = useQuery({
    queryKey: ['allowance-schedules', child.id],
    queryFn: async () => {
      const { data, error } = await api.GET('/allowance-schedules', {
        params: { query: { childId: child.id, includeInactive: true } },
      })
      if (error) throw error
      return data
    },
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['allowance-schedules', child.id] })
  }

  return (
    <section className="child-allowance">
      <h2>{child.displayName}</h2>
      <table>
        <thead>
          <tr>
            <th className="num">Amount</th>
            <th>Day</th>
            <th>Start</th>
            <th>End</th>
            <th>Active</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(schedulesQuery.data ?? []).map((s) => (
            <tr key={s.id} className={s.active ? '' : 'inactive'}>
              <td className="num money">{formatMoney(s.amount, exponentByCode)}</td>
              <td>{s.dayOfWeek}</td>
              <td>{s.startOn}</td>
              <td>{s.endOn ?? ''}</td>
              <td>{s.active ? 'Yes' : 'No'}</td>
              <td>{s.active && <ScheduleActions scheduleId={s.id} onDone={invalidate} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <CreateScheduleForm childId={child.id} onDone={invalidate} />
    </section>
  )
}

function ScheduleActions({ scheduleId, onDone }: { scheduleId: number; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'raise' | 'pause'>('idle')
  const [amount, setAmount] = useState('')
  const [effectiveOn, setEffectiveOn] = useState(new Date().toISOString().slice(0, 10))
  const [error, setError] = useState<string | null>(null)

  const raiseMutation = useMutation({
    mutationFn: async () => {
      const amountMinorUnits = parseMoneyInput(amount, 2) // allowance amounts are always USD
      if (amountMinorUnits === null) throw new Error(`"${amount}" isn't a valid USD amount`)
      const { error: apiError } = await api.PATCH('/allowance-schedules/{id}', {
        params: { path: { id: scheduleId } },
        body: { changeAmount: { amountMinorUnits, effectiveOn } },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setMode('idle')
      setError(null)
      onDone()
    },
    onError: (e: Error) => setError(e.message),
  })

  const pauseMutation = useMutation({
    mutationFn: async () => {
      const { error: apiError } = await api.PATCH('/allowance-schedules/{id}', {
        params: { path: { id: scheduleId } },
        body: { pause: { asOf: effectiveOn } },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setMode('idle')
      setError(null)
      onDone()
    },
    onError: (e: Error) => setError(e.message),
  })

  if (mode === 'idle') {
    return (
      <>
        <button type="button" onClick={() => setMode('raise')}>
          Change amount
        </button>{' '}
        <button type="button" onClick={() => setMode('pause')}>
          Pause
        </button>
      </>
    )
  }

  if (mode === 'raise') {
    return (
      <span className="inline-form">
        <input
          type="text"
          inputMode="decimal"
          placeholder="New USD amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <input type="date" value={effectiveOn} onChange={(e) => setEffectiveOn(e.target.value)} />
        <button type="button" onClick={() => raiseMutation.mutate()} disabled={raiseMutation.isPending}>
          Save
        </button>
        <button type="button" onClick={() => setMode('idle')}>
          Cancel
        </button>
        {error && <span className="error">{error}</span>}
      </span>
    )
  }

  return (
    <span className="inline-form">
      <input type="date" value={effectiveOn} onChange={(e) => setEffectiveOn(e.target.value)} />
      <button type="button" onClick={() => pauseMutation.mutate()} disabled={pauseMutation.isPending}>
        Confirm pause
      </button>
      <button type="button" onClick={() => setMode('idle')}>
        Cancel
      </button>
      {error && <span className="error">{error}</span>}
    </span>
  )
}

function CreateScheduleForm({ childId, onDone }: { childId: number; onDone: () => void }) {
  const [amount, setAmount] = useState('')
  const [dayOfWeek, setDayOfWeek] = useState('FRIDAY')
  const [startOn, setStartOn] = useState(new Date().toISOString().slice(0, 10))
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const amountMinorUnits = parseMoneyInput(amount, 2)
      if (amountMinorUnits === null) throw new Error(`"${amount}" isn't a valid USD amount`)
      const { error: apiError } = await api.POST('/allowance-schedules', {
        body: { childId, amountMinorUnits, dayOfWeek, startOn },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setAmount('')
      setError(null)
      onDone()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <form
      className="create-schedule"
      onSubmit={(e) => {
        e.preventDefault()
        mutation.mutate()
      }}
    >
      <input
        type="text"
        inputMode="decimal"
        placeholder="Weekly USD amount"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        required
      />
      <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
        {DAYS_OF_WEEK.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <input type="date" value={startOn} onChange={(e) => setStartOn(e.target.value)} required />
      <button type="submit" disabled={mutation.isPending}>
        New schedule
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  )
}
